import React from 'react';

import { translate } from '~l10n/client';
import { IconLock } from '~shared/components/icons/standard';
import { ShareQuizDialog } from '~teacher/components/ShareQuizDialog/ShareQuizDialog';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useGetWorkspaceQuiz } from '~teacher/hook/workspaces/useGetWorkspaceQuiz';
import { useGetWorkspaceSettings } from '~teacher/hook/workspaces/useGetWorkspaceSettings';
import { useSetWorkspaceQuizSharing } from '~teacher/hook/workspaces/useSetWorkspaceQuizSharing';

import { deriveWorkspaceShareState } from './deriveWorkspaceShareState';

interface Props {
  workspaceId: string;
  workspaceQuizId: string;
  onClose: () => void;
}

/**
 * Share dialog for a shared-library quiz. Reuses the personal `ShareQuizDialog`
 * and drives its state (toggle/note/link visibility) from the member's role,
 * the library `allow_soc_code` setting, and the quiz's `sharable` flag.
 */
export function WorkspaceShareQuizDialog({
  workspaceId,
  workspaceQuizId,
  onClose,
}: Props) {
  const quizQuery = useGetWorkspaceQuiz({ workspaceId, workspaceQuizId });
  const settingsQuery = useGetWorkspaceSettings({ workspaceId });
  const { permissions, isLoading: permsLoading } = useWorkspacePermissionsV2({
    workspaceId,
  });
  const { mutate, isLoading: isToggling } = useSetWorkspaceQuizSharing({
    workspaceId,
    workspaceQuizId,
  });

  const sharable = quizQuery.data?.quiz.sharable ?? false;
  const socNumber = quizQuery.data?.quiz.socNumber ?? '';
  const allowSocCode = settingsQuery.data?.allowSocCode ?? false;
  // Mirrors the backend gate (editWorkspaceSettings op) — Admin/Owner.
  const canManage = !!permissions.editWorkspaceSettings;

  const isLoading =
    quizQuery.isLoading || settingsQuery.isLoading || permsLoading;

  const { isSharingEnabled, isControlsDisabled, showDisabledNote } =
    deriveWorkspaceShareState({ canManage, allowSocCode, sharable });

  const controlsDisabled = isControlsDisabled || isLoading || isToggling;

  // ToggleSwitch still fires onChange while disabled (with the current value),
  // so guard here — a disabled toggle must not issue a PUT.
  const handleEnableSharing = () => {
    if (controlsDisabled) {
      return;
    }
    mutate(!sharable);
  };

  return (
    <ShareQuizDialog
      isOpen
      title={translate('Share Quiz')}
      isSharingEnabled={isSharingEnabled}
      socNumber={socNumber}
      isControlsDisabled={controlsDisabled}
      handleEnableSharing={handleEnableSharing}
      disabledNote={
        showDisabledNote && !isLoading ? (
          <div className="flex items-center gap-3 rounded-4 bg-amber-50 p-4 text-base leading-8 text-amber-900">
            <IconLock className="flex-none text-amber-700" />
            <span>{translate('Disabled by your library admin')}</span>
          </div>
        ) : undefined
      }
      closeDialog={onClose}
    />
  );
}
